import SHA512 from 'crypto-js/sha512';
import {getStorage, updateStorage} from './storage';

import {SIERRA_URL, SIERRA_HIV2_URL} from '../constants';

const QUERY = `
query align($sequences: [UnalignedSequenceInput]!) {
  viewer {
    currentVersion { text, publishDate },
    sequenceAnalysis(sequences: $sequences) {
      inputSequence {
        header
        SHA512
      }
      bestMatchingSubtype {
        displayWithoutDistance
      }
      strain {
        name
        display
      }
      validationResults {
        level
        message
      }
      alignedGeneSequences(includeGenes: [CA, PR, RT, IN]) {
        gene { name },
        mutations {
          position
          AAs
          isInsertion
          insertedNAs
          isUnusual
          isSDRM
          primaryType
          hasStop
          shortText
          isApobecMutation
          isAmbiguous
        },
        adjustedAlignedNAs
        adjustedAlignedAAs
      }
    }
  }
}`;

const QUERY_HIV2 = `
query align($sequences: [UnalignedSequenceInput]!) {
  viewer {
    currentVersion { text, publishDate },
    sequenceAnalysis(sequences: $sequences) {
      inputSequence {
        header
        SHA512
      }
      bestMatchingSubtype {
        displayWithoutDistance
      }
      strain {
        name
        display
      }
      validationResults {
        level
        message
      }
      alignedGeneSequences {
        gene { name },
        mutations {
          position
          AAs
          isInsertion
          insertedNAs
          isUnusual
          isSDRM
          primaryType
          hasStop
          shortText
          isApobecMutation
          isAmbiguous
        },
        adjustedAlignedNAs(targetStrain: "HIV1")
        adjustedAlignedAAs(targetStrain: "HIV1")
      }
    }
  }
}`;


function processSierraSequence(alignedSeq, accession) {
  const {
    inputSequence: {header, SHA512},
    bestMatchingSubtype: bmg,
    strain: {name: species},
    validationResults: vr,
    alignedGeneSequences
  } = alignedSeq;
  if (header !== SHA512) {
    console.warn(`Sequence SHA512 doesn't match: ${header} != ${SHA512}`);
  }
  let subtype = bmg ? bmg.displayWithoutDistance : null;
  return {
    rawSequenceUuid: accession,
    subtype,
    species,
    validationResults: vr.map(({level, message}) => `${level}: ${message}`),
    geneSequences: alignedGeneSequences.map(geneSeq => {
      const {
        gene: {name: gene},
        mutations,
        adjustedAlignedNAs,
        adjustedAlignedAAs
      } = geneSeq;

      // "re-fit" firstAA and lastAA to HXB2 numbering
      const alignedNAs = adjustedAlignedNAs.replace(/^\.+|\.+$/g, '');
      let alignedAAs = adjustedAlignedAAs.replace(/^\.+/, '');
      const firstAA = adjustedAlignedAAs.length - alignedAAs.length + 1;
      alignedAAs = alignedAAs.replace(/\.+$/, '');
      const lastAA = firstAA + alignedAAs.length - 1;

      return {
        firstAA, lastAA,
        gene, alignedNAs, alignedAAs,
        mutations
      };
    })
  };
}


export async function alignSequences(sequences, species, cachekey) {
  const newSequences = {};
  const allSequences = [];
  let _SIERRA_CACHE = {};
  if (cachekey) {
    const data = (await getStorage(cachekey));
    _SIERRA_CACHE = data._SIERRA_CACHE;
    cachekey = data.uuid;
  }
  for (const {header, sequence} of sequences) {
    const hashkey = SHA512(sequence).toString();
    if (!(hashkey in _SIERRA_CACHE)) {
      newSequences[hashkey] = {header: hashkey, sequence};
    }
    allSequences.push({header, hashkey});
  }

  let query_url = SIERRA_URL;
  let query_string = QUERY;
  let variables = {
    sequences: Object.values(newSequences),
    // includeGenes: ['PR', 'RT', 'IN', 'CA'],
  };
  if (species === 'HIV2') {
    query_url = SIERRA_HIV2_URL;
    query_string = QUERY_HIV2;
    variables = {sequences: Object.values(newSequences)};
  }

  if (Object.keys(newSequences).length > 0) {
    const request = new Request(
      query_url,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          operationName: 'align',
          query: query_string,
          variables: variables
        })
      }
    );
    const resp = await fetch(request);
    const {data: {viewer}} = await resp.json();
    for (const sa of viewer.sequenceAnalysis) {
      _SIERRA_CACHE[sa.inputSequence.header] = sa;
    }
    cachekey = await updateStorage({_SIERRA_CACHE, uuid: cachekey});
  }
  return [
    allSequences.map(
      ({header, hashkey}) => (
        processSierraSequence(_SIERRA_CACHE[hashkey], header)
      )
    ),
    cachekey];
}



// WEBPACK FOOTER //
// ./src/utils/sierra.js